<template>
  <v-container
    v-if="user"
    fluid
    pa-0
  >
    <v-row v-if="!userHasAddresses">
      <v-col
        class="text-justify"
        cols="12"
      >
        <div class="text-center">
          <div class="mb-6">
            <v-icon
              color="brand-primary"
              size="128"
            >
              mdi-map-marker-off
            </v-icon>
          </div>

          <h2>{{ $t('Account.Addresses.NoAddresses.Title') }}</h2>

          <h4 class="font-weight-regular mt-4">
            {{ $t('Account.Addresses.NoAddresses.Subtitle') }}
          </h4>

          <v-btn
            :large="$vuetify.breakpoint.xsOnly"
            :x-large="$vuetify.breakpoint.smAndUp"
            block
            class="my-4"
            color="brand-primary brand-green-dark--text"
            dark
            depressed
            @click="addAddress"
          >
            <v-icon left>
              {{ $t('Account.Addresses.Button.Add.Icon') }}
            </v-icon>
            <span class="brand-text-title">{{ $t('Account.Addresses.Button.Add.Title') }}</span>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <template v-else>
      <v-row>
        <v-col
          class="text-end"
          cols="12"
        >
          <v-btn
            :disabled="isFormSaving"
            :loading="isFormSaving"
            class="text-capitalize white--text"
            color="brand-primary"
            depressed
            large
            @click="addAddress"
          >
            <v-icon left>
              {{ $t('Account.Addresses.Button.Add.Icon') }}
            </v-icon>
            {{ $t('Account.Addresses.Button.Add.Title') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <template v-for="address in userAddresses">
          <v-col
            :key="`user-address-${address.Id}`"
            cols="12"
          >
            <v-card outlined>
              <v-card-text>
                <v-img
                  v-if="isCustomAddress(address)"
                  height="200"
                  position="center center"
                  src="/img/map/map-placeholder.png"
                  style="border: 1px solid #e2e2e2;"
                  width="100%"
                />

                <v-map
                  v-else
                  :address-marker="{lat: address.Lat, lng: address.Lng}"
                  :map-center="{lat: address.Lat, lng: address.Lng}"
                  height="200"
                  map-static
                  show-address-marker
                  width="1200"
                />
              </v-card-text>

              <v-card-text
                class="pt-0 align-start"
                style="min-height: 100px"
              >
                <v-container
                  fluid
                  pa-0
                >
                  <v-row dense>
                    <v-col
                      cols="12"
                      style="min-height: 32px"
                    >
                      <v-chip
                        v-if="address.Nickname"
                        class="mr-1"
                        color="pink"
                        label
                        small
                        text-color="white"
                      >
                        {{ address.Nickname }}
                      </v-chip>

                      <v-chip
                        v-if="address.IsDefault"
                        class="mr-1"
                        color="success"
                        label
                        small
                        text-color="white"
                      >
                        {{ $t('Account.Addresses.Default') }}
                      </v-chip>
                    </v-col>

                    <v-col cols="12">
                      <h5 class="text-body-1 font-weight-bold">
                        {{ getAddressFormatted(address) }}
                      </h5>
                    </v-col>

                    <v-col cols="12">
                      <div
                        v-if="address.Floor"
                        class="pr-2 d-inline-flex text-body-2"
                        style="white-space: nowrap;"
                      >
                        <h5 class="d-inline-flex text-body-2 font-weight-bold">
                          {{ $t('Account.Addresses.Floor') }}:
                        </h5>&nbsp;{{ address.Floor }}
                      </div>

                      <div
                        v-if="address.DoorBell"
                        class="d-inline-flex text-body-2"
                        style="white-space: nowrap;"
                      >
                        <h5 class="d-inline-flex text-body-2 font-weight-bold">
                          {{ $t('Account.Addresses.DoorBell') }}:
                        </h5>&nbsp;{{ address.DoorBell }}
                      </div>

                      <div
                        v-if="address.Comment"
                        class="d-flex text-body-2"
                        style="white-space: nowrap;"
                      >
                        <h5 class="d-inline-block text-body-2 font-weight-bold">
                          {{ $t('Account.Addresses.Comment') }}:
                        </h5>&nbsp;{{ address.Comment }}
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-divider class="mx-4" />

              <v-card-actions>
                <v-spacer />

                <v-btn
                  :dark="!address.isDeleting"
                  :disabled="address.isDeleting"
                  :loading="address.isDeleting"
                  class="text-capitalize"
                  color="red"
                  text
                  @click="deleteAddress(address)"
                >
                  {{ $t('Common.Button.Delete') }}
                </v-btn>

                <v-btn
                  v-if="!isCustomAddress(address) || (isCustomAddress(address) && acceptCustomAddresses)"
                  :disabled="address.isDeleting"
                  class="text-capitalize"
                  color="brand-primary"
                  text
                  @click="editAddress(address)"
                >
                  {{ $t('Common.Button.Edit') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </template>
      </v-row>
    </template>

    <account-add-edit-address-dialog
      :item="selectedAccountAddress"
      :visible.sync="addEditAddressDialogVisible"
      @form:save:success="onAddressSaveSuccess"
    />
  </v-container>
</template>

<script>
import AccountAddressesMixin from '@/theme/default/views/account/AccountAddresses.js'

export default {
  name  : 'AccountAddresses',
  mixins: [AccountAddressesMixin]
}
</script>

<style scoped src="@/theme/default/views/account/AccountAddresses.css"></style>
<style scoped>
</style>
